import React, { useState } from 'react';

export type VistaStampa = 'APPALTATORE' | 'ACQUIRENTE';
export type StampaTotali = 'CON_TOTALI' | 'SENZA_TOTALI';
export type StampaAllegati = 'CON_ALLEGATI' | 'SENZA_ALLEGATI';

interface ModalSceltaVistaStampaProps {
  preventivoNumero?: string;
  labelAppaltatore?: string | null;
  labelAcquirente?: string | null;
  /**
   * Se false, nasconde la sezione di scelta destinatario (intro + card)
   * e mostra solo le opzioni di stampa. La vista passata al callback è null.
   * Default: true (comportamento legacy per CONFIGURATO_COMMESSA con acquirente).
   */
  mostraSceltaVista?: boolean;
  onSelected: (vista: VistaStampa | null, totali: StampaTotali, allegati: StampaAllegati) => void;
  onClose: () => void;
}

/**
 * Modale che precede PrintModal per i preventivi CONFIGURATO_COMMESSA con acquirente.
 * L'utente sceglie destinatario (Appaltatore / Acquirente) e le opzioni di stampa
 * (totali e allegati inclusi o esclusi).
 *
 * Per preventivi senza acquirente (SEMPLICE / CONFIGURATO_COMMESSA senza acquirente)
 * passare `mostraSceltaVista={false}`: la modale mostra solo le opzioni di stampa
 * e il callback riceve vista=null.
 */
export default function ModalSceltaVistaStampa({
  preventivoNumero,
  labelAppaltatore,
  labelAcquirente,
  mostraSceltaVista = true,
  onSelected,
  onClose,
}: ModalSceltaVistaStampaProps) {
  const [vista, setVista] = useState<VistaStampa | null>(null);
  const [totali, setTotali] = useState<StampaTotali>('CON_TOTALI');
  const [allegati, setAllegati] = useState<StampaAllegati>('CON_ALLEGATI');

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--md">
        <div className="bop-modal__header">
          <h3 className="bop-modal__title">
            {mostraSceltaVista ? 'Scegli destinatario stampa' : 'Opzioni di stampa'}
            {preventivoNumero ? ` — ${preventivoNumero}` : ''}
          </h3>
          <button className="bop-modal__close" onClick={onClose}>×</button>
        </div>

        <div className="bop-modal__body">
          {mostraSceltaVista && (
            <>
              <p className="bop-text-muted bop-scelta-vista__intro">
                Questo preventivo è legato a un capitolato e ha un acquirente.
                Scegli per chi generare la stampa: i prezzi e i destinatari email
                della stampa cambiano di conseguenza.
              </p>

              <div className="bop-scelta-vista">
                <button
                  type="button"
                  className={`bop-scelta-vista__card${vista === 'APPALTATORE' ? ' bop-scelta-vista__card--selected' : ''}`}
                  onClick={() => setVista('APPALTATORE')}
                >
                  <div className="bop-scelta-vista__title">Appaltatore</div>
                  <div className="bop-scelta-vista__subtitle">
                    {labelAppaltatore || 'Impresa / immobiliare'}
                  </div>
                  <div className="bop-scelta-vista__desc">
                    Vista completa: capitolato + extra-capitolato, dettaglio dei costi
                    interni. La mail arriva solo al nominativo.
                  </div>
                </button>

                <button
                  type="button"
                  className={`bop-scelta-vista__card${vista === 'ACQUIRENTE' ? ' bop-scelta-vista__card--selected' : ''}`}
                  onClick={() => setVista('ACQUIRENTE')}
                >
                  <div className="bop-scelta-vista__title">Acquirente capitolato</div>
                  <div className="bop-scelta-vista__subtitle">
                    {labelAcquirente || 'Acquirente finale'}
                  </div>
                  <div className="bop-scelta-vista__desc">
                    Vista ridotta: solo extra-capitolato (capitolato = 0). Nessun dato
                    interno. La mail arriva ad acquirente + nominativo.
                  </div>
                </button>
              </div>
            </>
          )}

          <div className="bop-scelta-opzioni">
            <div className="bop-scelta-opzioni__title">Opzioni di stampa</div>

            <div className="bop-scelta-opzioni__row">
              <span className="bop-scelta-opzioni__label">Totali</span>
              <div className="bop-scelta-toggle">
                <button
                  type="button"
                  className={`bop-scelta-toggle__btn${totali === 'CON_TOTALI' ? ' bop-scelta-toggle__btn--active' : ''}`}
                  onClick={() => setTotali('CON_TOTALI')}
                >
                  Con totali
                </button>
                <button
                  type="button"
                  className={`bop-scelta-toggle__btn${totali === 'SENZA_TOTALI' ? ' bop-scelta-toggle__btn--active' : ''}`}
                  onClick={() => setTotali('SENZA_TOTALI')}
                >
                  Senza totali
                </button>
              </div>
            </div>

            <div className="bop-scelta-opzioni__row">
              <span className="bop-scelta-opzioni__label">Allegati</span>
              <div className="bop-scelta-toggle">
                <button
                  type="button"
                  className={`bop-scelta-toggle__btn${allegati === 'CON_ALLEGATI' ? ' bop-scelta-toggle__btn--active' : ''}`}
                  onClick={() => setAllegati('CON_ALLEGATI')}
                >
                  Con allegati
                </button>
                <button
                  type="button"
                  className={`bop-scelta-toggle__btn${allegati === 'SENZA_ALLEGATI' ? ' bop-scelta-toggle__btn--active' : ''}`}
                  onClick={() => setAllegati('SENZA_ALLEGATI')}
                >
                  Senza allegati
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button
            className="bop-btn-primary"
            disabled={mostraSceltaVista && vista === null}
            onClick={() => onSelected(mostraSceltaVista ? vista : null, totali, allegati)}
          >
            Conferma
          </button>
        </div>
      </div>
    </div>
  );
}
